import ObjectUtils from "../../utils/objectUtils";
import TypesUtils from "../../utils/typesUtils";
import { TIPO_ORDEN_CUSTOM } from "./ordenListaObjetosConId";

const FIELD_NOMBRE = "nombre";
const FIELD_ID = "id";

class ListaObjetosConId {
    constructor(jsonData, fnObjectConstructor) {
        // Map de pares (id,objeto)
        this.mapId = {};
        // lista de objetos
        this.list = [];
        // Map de pares (id,index en lista de objetos)
        this.mapIdIndex = {};

        if (jsonData) {
            jsonData.forEach((data) => {
                let newObj = fnObjectConstructor
                    ? fnObjectConstructor(data)
                    : data;
                let index = this.list.push(newObj);
                this.mapId[newObj.id] = newObj;
                this.mapIdIndex[newObj.id] = index - 1;
            });
        }
    }

    /**
     * Crea una instancia concreta de la lista. Debe ser implementado en las listas concretas que extiendan esta clase
     */
    createNewList() {
        throw new Error(
            "createNewList debe ser implementado en las listas concretas"
        );
    }

    /**
     * Retorna la cantidad de elementos en la lista
     */
    size() {
        return this.list.length;
    }

    /**
     * Retorna true sii el indice dado es valido para indexar en la lista
     */
    validIndex(i) {
        return TypesUtils.notUndefOrNull(i) && i >= 0 && i < this.size();
    }

    /**
     * Agrega el obj a la lista al final de `list`
     *
     * @param {*} obj
     * @returns Retorna el nuevo tamaño de la lista
     */
    add(obj) {
        if (this.mapId[obj.id])
            throw new Error("Objeto de id=" + obj.id + " ya ingresado");
        this.mapId[obj.id] = obj;
        let newLenght = this.list.push(obj);
        let newIndex = newLenght - 1;
        this.mapIdIndex[obj.id] = newIndex;
        return newLenght;
    }

    push(obj) {
        return this.add(obj);
    }

    /**
     * Retorna el objeto ubicado en la posicion dada por su indice
     * @param {number} index
     */
    get(index) {
        if (this.validIndex(index)) return this.list[index];
        return null;
    }

    /**
     * Retorna el nombre del objeto ubicado en la posicion dada por su indice
     * @param {number} index
     */
    getNombre(index) {
        if (this.validIndex(index) && this.list[index])
            return this.list[index].nombre;
        return null;
    }

    exists(id) {
        return this.mapId[id] !== undefined && this.mapId[id] !== null;
    }

    /**
     * Retorna el objeto de la lista con el id dado
     * @param {number} id id del objeto buscado
     */
    getById(id) {
        let o = this.mapId[id];
        return o ? o : null;
    }

    /**
     * Retorna el indice del objeto dado por su id. Si no existe retorna -1
     * @param {number} id
     */
    indexOf(id) {
        let res = this.mapIdIndex[id];
        return res >= 0 ? res : -1;
    }

    getFieldSeparated(fieldName, separator, ids) {
        let res = "";
        if (ids) {
            ids.list.forEach((id) => {
                let item = this.getById(id);
                if (item && item[fieldName]) {
                    res = res.concat(item[fieldName]).concat(separator);
                }
            });
        } else {
            this.list.forEach((item) => {
                if (item[fieldName]) res += item[fieldName] + separator;
            });
        }
        if (res.length > 0) res = res.slice(0, -1 * separator.length);

        return res;
    }

    getFieldsValsArrays(fieldNames, ids) {
        let lista = this;
        if (ids) lista = this.filterByIds(ids);
        let res = [];
        fieldNames.forEach((fname) => {
            let fvals = [];
            lista.list.forEach((item) => {
                item[fname] ? fvals.push(item[fname]) : fvals.push(null);
            });
            res.push(fvals);
        });
        return res;
    }

    getFieldValsArrays(fieldName, ids) {
        let lista = this;
        if (ids) lista = this.filterByIds(ids);
        let fvals = [];
        lista.list.forEach((item) => {
            item[fieldName] ? fvals.push(item[fieldName]) : fvals.push(null);
        });
        return fvals;
    }

    /**
     * Recibe un array de ids y retorna una ListaObjetosConId con los elementos cuyos ids esten en ids
     *
     * @param {array} ids
     */
    filterByIds(ids) {
        let list = this.createNewList();
        if (ids) {
            ids.forEach((id) => {
                let elem = this.getById(id);
                if (elem) list.add(elem);
            });
        }
        return list;
    }

    /**
     * Recibe un array de ids y retorna un Array con los elementos cuyos ids esten en ids
     *
     * @param {array} ids
     */
    filterByIdsArray(ids) {
        let list = [];
        if (ids) {
            ids.forEach((id) => {
                let elem = this.getById(id);
                if (elem) list.push(elem);
            });
        }
        return list;
    }

    /**
     * Retorna una lista separada por comas del valor de la prop "nombre" de cada item. Si ids se especifica se toman en
     * cuenta los items cuyos id esten en dicha lista
     *
     * @param {array} ids - Ids de los elementos a considerar
     */
    getNombresComaSep(ids) {
        return this.getFieldSeparated(FIELD_NOMBRE, ", ", ids);
    }

    /**
     * Retorna un array con dos elementos. El primero es un array con todos los valores de la prop "id".
     * El segundo es un array con todos los valores de la prop "nombre".
     * Si ids se especifica se toman en cuenta los items cuyos id esten en dicha lista
     *
     * @param {*} ids - Lista de ids que especifica que elementos se debe considerar
     */
    getIdsYNombres(ids) {
        return this.getFieldsValsArrays([FIELD_ID, FIELD_NOMBRE], ids);
    }

    /**
     * Retorna un array de ids
     *
     */
    getIds() {
        return this.getFieldsValsArrays([FIELD_ID])[0];
    }

    copy() {
        let res = new ListaObjetosConId();
        res.mapId = ObjectUtils.plainCopy(this.mapId);
        res.mapIdIndex = ObjectUtils.plainCopy(this.mapIdIndex);
        res.list = this.list.slice();
    }

    recreateMaps() {
        this.mapId = {};
        this.mapIdIndex = {};
        this.list.forEach((item, index) => {
            this.mapId[item.id] = item;
            this.mapIdIndex[item.id] = index;
        });
    }

    removeById(id) {
        let index = this.mapIdIndex[id];
        if (index >= 0 && index < this.list.length) {
            this.mapIdIndex[id] = undefined;
            this.mapId[id] = undefined;
            this.list.splice(index, 1);
            for (let i = index; i < this.list.length; i++) {
                this.mapIdIndex[this.list[i].id] = i;
                this.mapId[this.list[i].id] = this.list[i];
            }
        }
    }

    createListInstance(classType) {
        return classType ? new classType() : ListaObjetosConId.constructor();
    }

    /**
     *  Ordena la lista de objetos utilizando la funcion de comparacion dada. No afecta el objeto original.
     *
     * @param {*} cmpFunction
     */
    sortCopy(cmpFunction, classType) {
        let res;
        if (classType) res = new classType();
        else res = ListaObjetosConId.constructor();
        res.list = this.list.slice().sort(cmpFunction);
        res.recreateMaps();
        return res;
    }

    /**
     *  Ordena la lista de objetos utilizando la lista de ids dada. No afecta el objeto original.
     *
     * @param {*} ids
     */
    sortCopyCustom(ids, classType) {
        let res = this.createListInstance(classType);
        let mapSortedIdsIndex = {};
        ids.forEach((id, index) => {
            mapSortedIdsIndex[id] = index;
        });
        res.list = this.list.slice().sort((o1, o2) => {
            if (mapSortedIdsIndex[o1.id] < mapSortedIdsIndex[o2.id]) return -1;
            else if (mapSortedIdsIndex[o1.id] > mapSortedIdsIndex[o2.id])
                return 1;
            return 0;
        });
        res.recreateMaps();
    }

    /**
     * Retorna el resultado de la interseccion de esta lista con la dada.
     * No modifica la lista original.
     *
     * @param {ListaObjetosConId} listaObjetosConId
     * @param {type} classType Tipo de lista, descendiente de ListaObjetosConId. Si no se define se tomara
     * el tipo de esta clase ListaObjetosConId
     */
    intersect(listaObjetosConId, classType) {
        let res = this.createListInstance(classType);
        // Elijo la lista mas corta para recorrer
        this.list.forEach((item) => {
            if (listaObjetosConId.exists(item.id)) res.add(item);
        });
        return res;
    }

    // -------------------------------------------
    // ----- Operaciones de filtro y sorting -----
    // -------------------------------------------

    /**
     * Retorna una lista de objetos  filtradas segun los parametros de filtrado definidos en el argumento filtros.
     * No modifica la lista original.
     *
     * @param {import('./filtrosListaObjetosConId').default} filtros
     * @returns {ListaObjetosConId} Lista filtrada
     */
    filtrar() {
        // Actualmente no hay filtros para esta lista
        let res = new ListaObjetosConId();
        this.list.forEach((est) => {
            res.add(est);
        });
        return res;
    }

    /**
     * Retorna una lista ordenada segun los parametros dados en `orden`
     * No afecta el objeto original.
     *
     * @param {import('./ordenListaObjetosConId').default} orden Definicion del orden a aplicar
     * @returns {ListaObjetosConId} Lista ordenada
     */
    sort(orden) {
        if (orden.tipo === TIPO_ORDEN_CUSTOM)
            return this.sortCopyCustom(orden.ids);
        // Si el tipo de orden no se reconoce se retorna una copia de la lista
        return this.copy();
    }

    // ----------------------------------------------
    // ----- Funciones de colecciones genericas -----
    // ----------------------------------------------

    /**
     * Invoca la funcion `fn` sobre cada elemento de la lista.
     *
     * @param {callback} fn
     * @returns {*[]} Retorna un array donde cada elemento corresponde al resultado de la invocacion de `fn` sobre caad
     * elemento de la lista.
     */
    map(fn) {
        return this.list.map(fn);
    }

    /**
     * Invoca la funcion `fn` sobre cada elemento de la lista.
     * No retorna nada
     * @param {callback} fn
     */
    forEach(fn) {
        this.list.forEach(fn);
    }
}

export { ListaObjetosConId as default };
